import cn from 'classnames'
import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { RenderWhen } from '../renderWhen'

import { Content } from './components/content'
import { Header } from './components/header'
import { Item } from './components/item'
import { BodySharedBlock, HeaderSharedBlock } from './components/shared'
import { isBodySharedBlock, isHeaderSharedBlock, isItem } from './helpers'
import { TabsProps } from './models'

import styles from './Tabs.module.scss'

export const Tabs = memo<TabsProps>(
  ({
    children,
    classes,
    keepMounted,
    onItemSelect,
    renderCustomHeaderItem,
    renderCustomHeader,
    selectedItem = 0,
  }) => {
    const [activeIndex, setActiveIndex] = React.useState(selectedItem)

    const items = useMemo(
      () =>
        React.Children.toArray(children)
          .filter(isItem)
          .filter((item) => !item.props.isHidden),
      [children]
    )
    const bodySharedBlock = useMemo(
      () => React.Children.toArray(children).filter(isBodySharedBlock),
      [children]
    )
    const headerSharedBlock = useMemo(
      () => React.Children.toArray(children).filter(isHeaderSharedBlock),
      [children]
    )

    const itemMapping: number[] = useMemo(
      () =>
        items.reduce<number[]>((acc, child, index) => {
          if (isItem(child)) {
            acc.push(index)
          }
          return acc
        }, []),
      [items]
    )

    const handleHeaderItemClick = useCallback(
      (index: number) => {
        const outerIndex = itemMapping.indexOf(index)
        const child = items[index]
        if (isItem(child)) {
          setActiveIndex(index)

          if (onItemSelect) {
            onItemSelect(outerIndex)
          }
        }
      },
      [itemMapping, onItemSelect, items]
    )

    useEffect(() => {
      const innerIndex = itemMapping[selectedItem]
      setActiveIndex(innerIndex)
    }, [itemMapping, selectedItem])

    return (
      <div className={cn(styles.container, classes?.container)}>
        <RenderWhen condition={Boolean(renderCustomHeader)}>
          {renderCustomHeader?.({ activeIndex, onItemClick: handleHeaderItemClick, items })}
        </RenderWhen>
        <RenderWhen condition={!renderCustomHeader}>
          <Header
            activeIndex={activeIndex}
            onItemClick={handleHeaderItemClick}
            sharedBlock={headerSharedBlock}
            items={items}
            classes={classes}
            renderCustomHeaderItem={renderCustomHeaderItem}
          />
        </RenderWhen>
        <Content
          activeIndex={activeIndex}
          keepMounted={keepMounted}
          sharedBlock={bodySharedBlock}
          items={items}
          classes={classes}
        />
      </div>
    )
  }
) as React.NamedExoticComponent<TabsProps> & {
  Item: typeof Item
  BodySharedBlock: typeof BodySharedBlock
  HeaderSharedBlock: typeof HeaderSharedBlock
}

Tabs.Item = Item
Tabs.BodySharedBlock = BodySharedBlock
Tabs.HeaderSharedBlock = HeaderSharedBlock

Tabs.displayName = 'Tabs'
